import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type NotificationsAccessJSON = z.infer<typeof NotificationsAccessSchema.NotificationsAccessJSONZ>;

export type NotificationsAccessParams = z.infer<typeof NotificationsAccessSchema.NotificationsAccessZ>;

export default class NotificationsAccessSchema {
  public static readonly NotificationsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.NotificationsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly NotificationsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.NotificationsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): NotificationsAccessParams {
    return parseOrThrow(NotificationsAccessSchema.NotificationsAccessZ, values, {
      logError: true,
      schemaName: "NotificationsAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): NotificationsAccessJSON {
    return parseOrThrow(NotificationsAccessSchema.NotificationsAccessJSONZ, values, {
      logError: true,
      schemaName: "NotificationsAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is NotificationsAccessParams {
    try {
      NotificationsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is NotificationsAccessJSON {
    try {
      NotificationsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: NotificationsAccessJSON): NotificationsAccessParams {
    const parsed = parseOrThrow(NotificationsAccessSchema.NotificationsAccessJSONZ, data, {
      logError: true,
      schemaName: "NotificationsAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return NotificationsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: NotificationsAccessParams): NotificationsAccessJSON {
    const parsed = NotificationsAccessSchema.validate(notification);
    return NotificationsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    NotificationsAccessSchema.NotificationsAccessJSONZ,
    NotificationsAccessSchema.NotificationsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.NotificationsAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.NotificationsAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
